// ============================================================
// ANSICHTSSACHE-AGENT – Installation als App
// ============================================================
// Die eine Stelle, die weiss, ob und wie sich der Agent auf
// diesem Geraet ablegen laesst. Hinweis und Register in „Mein
// Konto“ fragen beide hier — zwei Fassungen derselben
// Browser-Kunde liefen sonst auseinander.
//
// Drei Faelle, und nur drei:
//
//   „angebot“  Chrome, Edge, Chromium am Rechner und auf
//              Android fragen selbst. Ein Knopf genuegt.
//   „ios“      Safari am Telefon kennt kein Angebot. Dort
//              fuehrt nur der Weg ueber „Teilen“, und den muss
//              man ansagen.
//   „safari“   Safari am Rechner legt die Seite ins Dock, auch
//              ueber ein Menue.
//
// Alles andere (Firefox am Rechner) kann es nicht — dort wird
// nichts angeboten. Ein Hinweis auf eine Moeglichkeit, die es
// im Browser des Lesers nicht gibt, ist kein Hinweis, sondern
// eine Sackgasse.
//
// Gelesen wird ueber `useSyncExternalStore` und nicht ueber
// `useState` im Effekt: der Browser IST hier der Zustand.
// Nebenbei loest das die Hydration — bis React laeuft, gilt die
// Server-Antwort („nichts bekannt“), und erst danach die des
// Geraets.
// ============================================================

"use client";

import { useCallback, useSyncExternalStore } from "react";
import { isNativeApp } from "@/lib/native-app";

/**
 * Das Ereignis, mit dem Chromium die Installation anbietet. Es
 * steht in keiner Typdefinition des Browsers, weil es kein
 * Standard ist — deshalb hier als eigener Typ statt als `any`.
 */
export type InstallationsEreignis = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

declare global {
  interface Window {
    /**
     * Das Angebot, im `<head>` abgefangen. Chromium wirft es
     * einmal und frueh — meist bevor React ueberhaupt laeuft.
     * Wer erst beim Einhaengen zuhoert, hoert nichts. Gesetzt
     * wird es vom Skript im Wurzel-Layout.
     */
    __agentInstallationsAngebot?: InstallationsEreignis | null;
  }
}

/**
 * Das eigene Ereignis, mit dem sich alle Leser ueber einen
 * Wechsel verstaendigen — das Skript im Layout, der Hinweis und
 * das Register. `beforeinstallprompt` selbst kommt nur einmal.
 */
export const ANGEBOT_EREIGNIS = "agent:installierbar";

export type Installationsweg = "angebot" | "ios" | "safari" | "keiner";

export type Installationsstand = {
  /** Die Anwendung laeuft bereits in einem eigenen Fenster. */
  installiert: boolean;
  weg: Installationsweg;
  /**
   * Fragt den Browser. Gibt zurueck, ob die Person zugestimmt
   * hat — bei jedem anderen Weg als „angebot“ immer `false`.
   */
  installieren: () => Promise<boolean>;
};

/** Der Satz, der sagt, was von Hand zu tun ist. */
export const ANLEITUNG: Record<Installationsweg, string | null> = {
  angebot: null,
  ios: "In Safari auf das Teilen-Zeichen tippen und „Zum Home-Bildschirm“ wählen.",
  safari: "In Safari im Menü „Ablage“ auf „Zum Dock hinzufügen“ gehen.",
  keiner: null,
};

/**
 * Merkt sich die Installation fuer die Dauer der Seite.
 *
 * Der Browser meldet sie mit `appinstalled` — aber das Fenster,
 * in dem man dabei steht, bleibt ein Browserfenster. Ohne dieses
 * Merkzeichen behauptete die Seite unmittelbar nach dem
 * Installieren weiter, die App sei nicht installiert.
 */
let installationGemeldet = false;

function eigenesFenster(): boolean {
  return (
    installationGemeldet ||
    // Die App „Agent“ (iPhone) zeigt den Agent in einer Web-Ansicht —
    // sie IST die installierte Fassung, seit dem 20.10.2026 an Stelle
    // der PWA (`lib/native-app.ts`).
    isNativeApp(navigator.userAgent) ||
    window.matchMedia("(display-mode: standalone)").matches ||
    // `standalone` ist Safaris eigene Auskunft und aelter als
    // die Medienabfrage; am iPhone ist sie bis heute die
    // verlaessliche.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function wegErmitteln(): Installationsweg {
  const kennung = navigator.userAgent;
  // iPadOS gibt sich seit Jahren als Mac aus. Nur die Zahl der
  // Beruehrungspunkte verraet, dass ein Finger davorsteht.
  const apfelGeraet =
    /iphone|ipad|ipod/i.test(kennung) ||
    (/macintosh/i.test(kennung) && navigator.maxTouchPoints > 1);
  if (apfelGeraet) return "ios";

  const safari = /safari/i.test(kennung) && !/chrome|chromium|crios|fxios|edg|opr/i.test(kennung);
  return safari ? "safari" : "keiner";
}

function abonniereFenstermodus(melden: () => void): () => void {
  const modus = window.matchMedia("(display-mode: standalone)");
  const beiInstallation = () => {
    installationGemeldet = true;
    melden();
  };
  modus.addEventListener("change", melden);
  window.addEventListener("appinstalled", beiInstallation);
  return () => {
    modus.removeEventListener("change", melden);
    window.removeEventListener("appinstalled", beiInstallation);
  };
}

function abonniereAngebot(melden: () => void): () => void {
  // Falls das Skript im Layout nicht zum Zug kam — etwa, weil
  // der Browser das Ereignis spaeter ein zweites Mal wirft.
  const beiBrowserAngebot = (ereignis: Event) => {
    ereignis.preventDefault();
    window.__agentInstallationsAngebot = ereignis as InstallationsEreignis;
    melden();
  };
  const beiInstallation = () => {
    window.__agentInstallationsAngebot = null;
    melden();
  };
  window.addEventListener(ANGEBOT_EREIGNIS, melden);
  window.addEventListener("beforeinstallprompt", beiBrowserAngebot);
  window.addEventListener("appinstalled", beiInstallation);
  return () => {
    window.removeEventListener(ANGEBOT_EREIGNIS, melden);
    window.removeEventListener("beforeinstallprompt", beiBrowserAngebot);
    window.removeEventListener("appinstalled", beiInstallation);
  };
}

/** Der Weg wechselt nicht, solange die Seite steht. */
const ohneAbo = () => () => {};

export function useInstallation(): Installationsstand {
  const installiert = useSyncExternalStore(abonniereFenstermodus, eigenesFenster, () => false);
  const angebot = useSyncExternalStore(
    abonniereAngebot,
    () => window.__agentInstallationsAngebot ?? null,
    () => null,
  );
  const geraeteweg = useSyncExternalStore<Installationsweg>(
    ohneAbo,
    wegErmitteln,
    () => "keiner" as const,
  );

  const installieren = useCallback(async () => {
    const angebot = window.__agentInstallationsAngebot;
    if (!angebot) return false;

    await angebot.prompt();
    const { outcome } = await angebot.userChoice;

    // Das Angebot gilt nur einmal. Nach der Frage ist es
    // verbraucht, gleich wie die Antwort ausfiel — und alle, die
    // es lesen, muessen das erfahren.
    window.__agentInstallationsAngebot = null;
    window.dispatchEvent(new Event(ANGEBOT_EREIGNIS));

    return outcome === "accepted";
  }, []);

  return { installiert, weg: angebot ? "angebot" : geraeteweg, installieren };
}
